import type { ErrorView } from '@krp/client-runtime'

import type { CommandStatus } from '../../../shared/model/commandOutcome'

/**
 * Модель поставок 1С (UB1c). Данные — то, что декодер вынул из ответа службы
 * (они живут только в кэше RTK Query); намерение — то, что набрал и выбрал
 * человек (слайс `supplies`). Ссылки 1С внутри ответа уже проверены на rid:
 * ссылка другой базы до модели не доходит.
 */

/** Лестница готовности черновика и состояния саги проведения (B3b `SupplyStatus`). */
export const SUPPLY_STATUSES = [
  'Draft',
  'Imported',
  'Matched',
  'Priced',
  'Allocated',
  'Posting',
  'Posted',
  'PartiallyPosted',
  'Failed',
] as const
export type SupplyStatus = (typeof SUPPLY_STATUSES)[number]

export const MATCH_STATUSES = ['NotMatched', 'Matched', 'Ambiguous', 'New'] as const
export type MatchStatus = (typeof MATCH_STATUSES)[number]

/** Ссылка 1С внутри ОДНОЙ базы: rid проверен декодером, остаётся ключ. */
export interface OneCRef {
  remoteKey: string
}

export interface PriceType {
  ref: OneCRef
  name: string
}

export interface PriceTypesDirectory {
  items: PriceType[]
  lastMessage: string | null
  isReady: boolean
}

export interface SupplyListItem {
  id: string
  number: string
  status: SupplyStatus
  statusName: string
  documentDate: string
  supplierName: string
  supplierInvoiceNumber: string | null
  warehouseName: string
  linesCount: number
  amountTotal: number
  updatedAt: string
  postedAt: string | null
  withoutSupplier: boolean
}

/** Страница курсора gateway-paging-v1. */
export interface CursorPage<T> {
  items: T[]
  nextCursor: string | null
  complete: boolean
  observedAt: string
}

export interface SupplySummary {
  linesTotal: number
  linesMatched: number
  linesNew: number
  linesAmbiguous: number
  linesNotMatched: number
  linesIgnored: number
  quantityTotal: number
  quantityAllocated: number
  quantityUnallocated: number
  amountTotal: number
  documentsToCreate: number
  blockers: string[]
}

export interface Supply {
  id: string
  number: string
  status: SupplyStatus
  statusName: string
  supplierRef: OneCRef | null
  supplierName: string
  supplierInvoiceNumber: string | null
  withoutSupplier: boolean
  documentDate: string
  warehouseRef: OneCRef | null
  warehouseName: string
  comment: string | null
  importedFileId: string | null
  allowUnallocatedRemainder: boolean
  postingLockedBy: string | null
  postedAt: string | null
  lastPostingAttempt: number
  updatedAt: string
  updatedBy: string | null
  version: number
  summary: SupplySummary
}

export interface LineAllocation {
  warehouseRef: OneCRef | null
  warehouseName: string
  quantity: number
}

export interface LinePrice {
  priceTypeRef: OneCRef | null
  priceTypeName: string
  currentPrice: number | null
  newPrice: number | null
  isManualOverride: boolean
}

export interface MatchCandidate {
  productId: string
  name: string
  score: number
}

export interface SupplyLine {
  id: string
  lineNumber: number
  rawName: string | null
  rawArticle: string | null
  rawBarcode: string | null
  quantity: number
  purchasePrice: number
  amount: number
  unitName: string | null
  matchStatus: MatchStatus
  productId: string | null
  matchedBy: string | null
  matchSource: string
  oneCName: string | null
  createInResource: boolean
  isIgnored: boolean
  notes: string | null
  version: number
  allocations: LineAllocation[]
  prices: LinePrice[]
  candidates: MatchCandidate[]
  allocatedQuantity: number
  isFullyAllocated: boolean
}

export interface AllocationWarehouse {
  ref: OneCRef
  name: string
  organizationName: string
  isDefault: boolean
  isMain: boolean
  quantityTotal: number
}

export interface AllocationRow {
  lineId: string
  lineNumber: number
  name: string | null
  quantity: number
  allocated: number
  unallocated: number
  isIgnored: boolean
  version: number
  cells: Record<string, number>
}

export interface AllocationMatrix {
  warehouses: AllocationWarehouse[]
  rows: AllocationRow[]
  unallocatedTotal: number
  documentsToCreate: number
}

export interface AllocationProblem {
  kind: string
  lineNumber: number
  lineName: string | null
  message: string
}

export interface PostingDocument {
  kind: string
  warehouseName: string
  organizationName: string
  sourceWarehouseName: string | null
  linesCount: number
  quantityTotal: number
  amountTotal: number
}

export interface PostingPreview {
  documents: PostingDocument[]
  pricesToWrite: number
  warnings: string[]
  problems: AllocationProblem[]
  resourceBlockers: string[]
  canPost: boolean
  previewHash: string
  headline: string
}

export interface PostingEntry {
  id: string
  stepKind: string
  status: string
  resourceEntityName: string | null
  httpStatus: number | null
  /** Текст 1С — дословно (ADR-8). */
  resourceErrorText: string | null
  startedAt: string
  correlationId: string | null
}

export interface PostedDocument {
  kind: string
  warehouseName: string
  sourceWarehouseName: string | null
  state: string
  documentNumber: string | null
  resourceErrorText: string | null
}

export interface PostingProgress {
  attemptNumber: number
  isRunning: boolean
  totalSteps: number
  completedSteps: number
  failedSteps: number
  skippedSteps: number
  currentStepKind: string | null
  entries: PostingEntry[]
  documents: PostedDocument[]
  finalStatus: string | null
}

export interface PostingAttempt {
  attemptNumber: number
  startedAt: string
  finishedAt: string | null
  total: number
  succeeded: number
  failed: number
  skipped: number
  steps: { stepKind: string; stepKindName: string; status: string; statusName: string; documentNumber: string | null; resourceErrorText: string | null }[]
}

export interface AuditEntry {
  id: string
  occurredAt: string
  actionName: string
  lineNumber: number | null
  fieldName: string | null
  oldValue: string | null
  newValue: string | null
  comment: string | null
  userName: string | null
  sourceName: string
}

export interface OneCReceipt {
  refKey: string
  number: string | null
  date: string | null
  posted: boolean
  supplierName: string
  warehouseName: string
  sum: number
  linesCount: number
}

// --- результаты команд, которые экран показывает -------------------------------------------

export interface CreatedSupply {
  id: string
  number: string
}

export interface FillResult {
  fileName: string
  totalRows: number
  added: number
  updated: number
  skippedExisting: number
  removed: number
  skipped: { sourceRowNumber: number; name: string | null; reason: string }[]
}

export interface PriceFactorResult {
  applied: boolean
  linesAffected: number
  linesSkipped: number
  oldAmountTotal: number
  newAmountTotal: number
}

/** Итог массового заполнения распределения (`AllocationFillPreviewDto`): применено ли и сколько строк. */
export interface AllocationFillResult {
  applied: boolean
  linesAffected: number
  warnings: string[]
}

export interface StartedPosting {
  attemptNumber: number
}

/** Итог команды, который нужен экрану; остальные команды отвечают «готово» и перечитывают кэш. */
export type SupplyCommandResult =
  | { kind: 'done'; warnings: string[]; lineVersion: number | null }
  | { kind: 'created'; supply: CreatedSupply }
  | { kind: 'filled'; fill: FillResult }
  | { kind: 'factor'; factor: PriceFactorResult }
  | { kind: 'allocated'; fill: AllocationFillResult }
  | { kind: 'posting'; posting: StartedPosting }

// --- намерение ----------------------------------------------------------------------------

export type SupplySection = 'lines' | 'allocation' | 'prices' | 'posting' | 'history'

export interface SupplyListFilter {
  status: SupplyStatus | ''
  number: string
}

export const EMPTY_LIST_FILTER: SupplyListFilter = { status: '', number: '' }

export interface LineFilter {
  search: string
  onlyProblems: boolean
}

export const EMPTY_LINE_FILTER: LineFilter = { search: '', onlyProblems: false }

/** Поля строки, которые правятся в таблице. Значения — как набраны (строкой). */
export type LineField = 'quantity' | 'purchasePrice' | 'amount' | 'notes'

/**
 * Черновик правки: дельта к версии, с которой человек начал. Уходит на сервер
 * debounce-listener-ом; при 409 остаётся и показывает серверную версию рядом.
 */
export type DraftState = 'pending' | 'saving' | 'failed' | 'unknown' | 'conflict'

export interface LineDraft {
  key: string
  resourceId: string
  supplyId: string
  lineId: string
  values: Partial<Record<LineField, string>>
  baseVersion: number
  /** Решение человека, когда количество меньше распределённого. */
  allocationPolicy: 'Clear' | 'Proportional' | null
  /** Ревизия настроек базы, по которой человек правил (ответ службы этой базы). */
  configurationVersion: string
  state: DraftState
  error: ErrorView | null
  /** Сколько раз набор менялся: ответ на старый набор не снимает новый. */
  revision: number
  /** Идентичность последней отправки: тот же набор после неизвестного исхода уходит тем же ключом. */
  sent: SentIdentity | null
}

export interface SentIdentity {
  operationId: string
  revision: number
  baseVersion: number
}

export interface AllocationDraft {
  key: string
  resourceId: string
  supplyId: string
  lineId: string
  /** Ключ склада → набранное количество. */
  cells: Record<string, string>
  /** Серверные доли строки на момент начала правки: недостающие склады уходят как есть. */
  base: Record<string, number>
  baseVersion: number
  configurationVersion: string
  state: DraftState
  error: ErrorView | null
  revision: number
  sent: SentIdentity | null
}

export type HeaderField = 'documentDate' | 'supplierInvoiceNumber' | 'comment' | 'warehouseKey'

/**
 * Черновик шапки: набранное и серверные значения ЭТИХ полей на момент начала
 * правки. Конфликт — когда изменилось само поле на сервере, а не версия
 * поставки: её двигают и собственные автосохранения строк (xmin поставки).
 */
export interface HeaderDraft {
  values: Partial<Record<HeaderField, string>>
  base: Partial<Record<HeaderField, string>>
}

export interface CreateForm {
  documentDate: string
  warehouseKey: string
  withoutSupplier: boolean
  supplierKey: string
  supplierName: string
  supplierInvoiceNumber: string
  comment: string
}

export interface AddLineForm {
  name: string
  article: string
  barcode: string
  quantity: string
  purchasePrice: string
}

export const EMPTY_ADD_LINE: AddLineForm = { name: '', article: '', barcode: '', quantity: '', purchasePrice: '' }

export type FillMode = 'Append' | 'Merge' | 'Replace'

export interface SupplyForms {
  addLine: AddLineForm
  invoiceFileId: string
  invoiceMode: FillMode
  priceFactor: string
  fillWarehouseKey: string
  /** Ключ «строка|вид цены» → набранная новая цена. */
  prices: Record<string, string>
  overrideProduct: Record<string, string>
}

export const EMPTY_FORMS: SupplyForms = {
  addLine: EMPTY_ADD_LINE,
  invoiceFileId: '',
  invoiceMode: 'Append',
  priceFactor: '',
  fillWarehouseKey: '',
  prices: {},
  overrideProduct: {},
}

/** Какая команда. Подпись и путь знает слой api; здесь — только смысл. */
export type SupplyCommandKind =
  | 'create'
  | 'update'
  | 'delete'
  | 'clone'
  | 'import-invoice'
  | 'add-line'
  | 'update-line'
  | 'delete-line'
  | 'set-ignored'
  | 'match'
  | 'override-match'
  | 'reset-match'
  | 'mark-new'
  | 'price-factor'
  | 'set-prices'
  | 'set-line-allocations'
  | 'default-warehouse'
  | 'fill-all-to-one'
  | 'fill-evenly'
  | 'fill-clear'
  | 'post'

export interface SupplyCommand {
  /** `rid|supplyId|kind[|detail]` — одна команда на намерение. */
  key: string
  resourceId: string
  /** Пусто у создания. */
  supplyId: string
  kind: SupplyCommandKind
  lineId: string | null
  /** Тело `data` команды — ровно то, что уйдёт и уйдёт ещё раз при проверке исхода. */
  data: unknown
  /** Параметры адреса (`force`, `documentDate`). */
  query: Record<string, string> | null
  operationId: string
  idempotencyKey: string
  expectedConfigurationVersion: string
  status: CommandStatus
  resendable: boolean
  error: ErrorView | null
  result: SupplyCommandResult | null
  /** Проведение: номер последней попытки, известный ДО нажатия. Новее — значит, POST дошёл. */
  baseline: number | null
  /** Создание из накладной: файл, которым заполнить строки новой поставки после подтверждения создания. */
  invoiceFileId: string | null
}

export interface SuppliesIntentState {
  listFilter: Record<string, SupplyListFilter>
  /** rid → сколько страниц снимка списка попросил человек («Показать ещё»). */
  listPages: Record<string, number>
  section: Record<string, SupplySection>
  lineFilter: Record<string, LineFilter>
  headerDrafts: Record<string, HeaderDraft>
  lineDrafts: Record<string, LineDraft>
  allocationDrafts: Record<string, AllocationDraft>
  createForms: Record<string, CreateForm>
  forms: Record<string, SupplyForms>
  commands: Record<string, SupplyCommand>
  historyPages: Record<string, number>
  /**
   * Версия строки, подтверждённая ответом НАШЕЙ записи (ключ строки → версия).
   * Следующая правка начинается с неё, а не с ещё не перечитанного кэша: иначе
   * своё же сохранение выглядело бы чужой правкой (409).
   */
  savedVersions: Record<string, number>
}
